import { css } from '@emotion/css'
import { Accordion, Group, Checkbox as MantineCheckbox, useMantineTheme } from '@mantine/core'
import { serviceCenterStore } from 'App/ServiceCenter/store/serviceCenterStore'
import type { TNodeType } from 'App/ServiceCenter/types'
import { getNodeTypeLabel } from 'App/ServiceCenter/utils/getNodeTypeLabel'
import { Checkbox } from 'components/Checkbox/Checkbox'
import { translate } from 'i18n/i18n'
import React, { useMemo } from 'react'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'

type ServiceIssueSummaryProps = {
  selectedIssueIds: number[]
  onIssueSelectionChange: (selectedIssueIds: number[]) => void
}

export const ServiceIssueSummary: React.FC<ServiceIssueSummaryProps> = ({
  selectedIssueIds,
  onIssueSelectionChange,
}) => {
  const theme = useMantineTheme()
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)

  const serviceIssuesByMapPropertyId = serviceCenterStore.useSelector((s) =>
    serviceCenterStore.selectors.getSelectedPropertyServiceIssues(s, selectedPropertyId),
  )

  const issueGroupedByNodeTypes = React.useMemo(() => {
    const results: { [nodeType: string]: { issueTypeId: number; label: string; count: number }[] } = {}

    Object.values(serviceIssuesByMapPropertyId).forEach((serviceIssue) => {
      serviceIssue.forEach((issue) => {
        const { nodeType, nodeServiceIssueTypeId, nodeServiceIssueTypeTitle } = issue

        // Group by node type and issue title
        if (!results[nodeType]) results[nodeType] = []

        const idx = results[nodeType].findIndex((item) => item.issueTypeId === nodeServiceIssueTypeId)

        if (idx === -1) {
          results[nodeType].push({
            issueTypeId: nodeServiceIssueTypeId,
            label: nodeServiceIssueTypeTitle,
            count: 1,
          })
        } else {
          results[nodeType][idx].count += 1
        }
      })
    })

    // Sort issue types within each node type by title
    Object.keys(results).forEach((nodeType) => {
      results[nodeType].sort((a, b) => a.label.localeCompare(b.label))
    })

    return results
  }, [serviceIssuesByMapPropertyId])

  const serviceIssueCount = useMemo(() => {
    let count = 0

    Object.values(serviceIssuesByMapPropertyId).forEach((nodeIssues) => {
      count += nodeIssues.length
    })

    return count
  }, [serviceIssuesByMapPropertyId])

  // Checkboxes from Mantine require string values
  const selectedIssueIdsAsString = useMemo(() => selectedIssueIds.map((id) => String(id)), [selectedIssueIds])
  const hasMultipleNodeTypes = Object.keys(issueGroupedByNodeTypes).length > 1

  if (Object.keys(issueGroupedByNodeTypes).length === 0) {
    return null
  }

  return (
    <Accordion
      chevronPosition="left"
      chevronSize={20}
      classNames={{
        // hover matches the base so Mantine's hover tint doesn't show
        control: css({
          'backgroundColor': theme.colors.midnight[0],
          'color': 'white',
          '&:hover': {
            backgroundColor: theme.colors.midnight[0],
            color: 'white',
          },
        }),
      }}
      styles={{
        label: {
          padding: '8px 0',
        },
        content: { padding: 0 },
      }}
    >
      <Accordion.Item value="issueOverview">
        <Accordion.Control>
          <Group justify="space-between">
            <span>{translate.phrases.placeholder('Issues Overview')}</span>
            <span>{serviceIssueCount}</span>
          </Group>
        </Accordion.Control>

        <Accordion.Panel>
          <Accordion
            multiple={true}
            variant="default"
            chevronPosition="left"
            chevronSize={20}
            defaultValue={!hasMultipleNodeTypes ? [Object.keys(issueGroupedByNodeTypes)[0]] : []}
            classNames={{
              control: css({
                'backgroundColor': theme.colors.grey[1],
                'color': 'black',
                '&:hover': {
                  backgroundColor: theme.colors.grey[1],
                },
              }),
            }}
            styles={{
              label: {
                padding: '8px 0',
              },
              content: { padding: 0 },
            }}
          >
            {Object.entries(issueGroupedByNodeTypes).map(([nodeType, issues]) => {
              const issueTotalCountByNodeType = issues.reduce((acc, { count }) => acc + count, 0)

              return (
                <Accordion.Item key={nodeType} value={nodeType}>
                  {hasMultipleNodeTypes && (
                    <Accordion.Control>
                      <Group justify="space-between">
                        <span>{`${getNodeTypeLabel(nodeType as TNodeType)}`}</span>

                        <span>{issueTotalCountByNodeType}</span>
                      </Group>
                    </Accordion.Control>
                  )}

                  <Accordion.Panel>
                    <MantineCheckbox.Group
                      value={selectedIssueIdsAsString}
                      onChange={(value) => onIssueSelectionChange(value.map((id) => +id))}
                    >
                      {issues.map(({ issueTypeId, label, count }) => (
                        <div
                          key={issueTypeId}
                          style={{
                            borderBottom: `1px solid ${theme.colors.grey[1]}`,
                            padding: '4px 16px 4px 10px',
                          }}
                        >
                          <Group justify="space-between">
                            <Checkbox label={label} value={String(issueTypeId)} />
                            <span>{count}</span>
                          </Group>
                        </div>
                      ))}
                    </MantineCheckbox.Group>
                  </Accordion.Panel>
                </Accordion.Item>
              )
            })}
          </Accordion>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}
